import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import {
  Typography,
  Box,
  CssBaseline,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
} from '@mui/material';
import { Phone, PauseCircle, PlayArrow, Download, Description as DescriptionIcon, Close } from '@mui/icons-material';
import axios from 'axios';
import { global_link } from '../Components/Globallink';
import WebSocketContext from '../Context/WebSocket';

const Calls = () => {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [calltypeFilter, setCalltypeFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [agentUsername, setAgentUsername] = useState('');
  const [custmerNo, setCustmerNo] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [currentAudio, setCurrentAudio] = useState(null);
  const [currentRecordingId, setCurrentRecordingId] = useState(null);
  const [transcriptOpen, setTranscriptOpen] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState(null);

  const api = axios.create({ baseURL: global_link });
  const authToken = process.env.AUTH_TOKEN;

  // ── Use shared context socket — no throwaway socket ───────────────────────
  const { socket } = useContext(WebSocketContext);

  // ── fetchData ref — always points to latest version with current params ───
  const fetchDataRef = useRef(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const authyToken = localStorage.getItem(authToken);
      const response = await api.get('/call/getcalllogs', {
        params: {
          search: searchTerm,
          call_type: calltypeFilter,
          start_date: startDate,
          end_date: endDate,
          limit: rowsPerPage,
          offset: page * rowsPerPage,
        },
        headers: { Authorization: `Bearer ${authyToken}` },
      });
      setData(response.data.data);
      setTotalItems(response.data.total);
    } catch (error) {
      console.error('Error fetching call logs:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, calltypeFilter, startDate, endDate, page, rowsPerPage, authToken]);

  // Keep ref in sync with latest fetchData every time params change
  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  // Run fetchData when params change
  useEffect(() => {
    if (startDate && endDate && new Date(endDate) < new Date(startDate)) {
      console.error('End date cannot be earlier than start date.');
      return;
    }
    fetchData();
  }, [fetchData]);

  // ── Socket: NEW_CALL_LOG_UPDATE — uses ref, re-syncs on reconnect ─────────
  useEffect(() => {
    if (!socket) return;

    const handleCallLogUpdate = () => {
      fetchDataRef.current?.();
    };

    socket.on('connect', handleCallLogUpdate);
    socket.on('NEW_CALL_LOG_UPDATE', handleCallLogUpdate);

    return () => {
      socket.off('NEW_CALL_LOG_UPDATE', handleCallLogUpdate);
      socket.off('connect', handleCallLogUpdate);
    };
  }, [socket]);

  // ── Window event from DialBar ─────────────────────────────────────────────
  useEffect(() => {
    const handleUpdateCallsData = () => fetchDataRef.current?.();
    window.addEventListener('updateCallsDataEvent', handleUpdateCallsData);
    return () => window.removeEventListener('updateCallsDataEvent', handleUpdateCallsData);
  }, []);

  // ── Audio playback ────────────────────────────────────────────────────────
  const handleAudioEnd = useCallback(() => {
    setActiveId(null);
    setCurrentAudio(null);
    setCurrentRecordingId(null);
  }, []);

  const togglePlay = useCallback((recordingId) => {
    if (currentAudio && !currentAudio.paused) {
      if (currentRecordingId === recordingId) {
        currentAudio.pause();
        setActiveId(null);
        return;
      } else {
        currentAudio.pause();
        currentAudio.currentTime = 0;
        setActiveId(null);
      }
    }

    if (currentRecordingId === recordingId && currentAudio && currentAudio.paused) {
      currentAudio.play()
        .then(() => setActiveId(recordingId))
        .catch(console.error);
      return;
    }

    if (currentAudio) {
      currentAudio.removeEventListener('ended', handleAudioEnd);
    }

    const audio = new Audio(`${global_link}/uploads/recordings/${recordingId}.webm`);
    setCurrentAudio(audio);
    setCurrentRecordingId(recordingId);

    audio.addEventListener('ended', handleAudioEnd);
    audio.play()
      .then(() => setActiveId(recordingId))
      .catch(console.error);
  }, [currentAudio, currentRecordingId, handleAudioEnd]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.removeEventListener('ended', handleAudioEnd);
      }
    };
  }, [currentAudio, handleAudioEnd]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  function maskNumber(num) {
    if (!num) return "";
    const str = String(num);
    if (str.length <= 5) return str;
    const start = str.slice(0, 2);
    const end = str.slice(-2);
    const hidden = "*".repeat(Math.max(3, str.length - 4));
    return start + hidden + end;
  }

  const handleDownload = async (call_id, call_from, call_to, recordingId) => {
    const maskedFrom = maskNumber(call_from);
    const maskedTo = maskNumber(call_to);
    const audioUrl = `${global_link}/uploads/recordings/${recordingId}.webm`;
    try {
      const response = await fetch(audioUrl);
      const blob = await response.blob();
      const downloadLink = document.createElement('a');
      downloadLink.href = URL.createObjectURL(blob);
      downloadLink.download = `recording_${maskedFrom} - ${maskedTo}_${call_id}.webm`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
      URL.revokeObjectURL(downloadLink.href);
    } catch (error) {
      console.error("Download failed:", error);
      alert("Failed to download the file. Please try again.");
    }
  };

  const copyPhoneNumber = (call_from, call_to) => {
    const phoneNumber = call_from.length > 5 ? call_from : call_to;
    window.dispatchEvent(new CustomEvent('copyPhoneNumber', { detail: phoneNumber }));
  };

  const getDurationColor = (duration) => {
    if (duration === 'Missed') return '#ff5722';
    if (duration === 'Busy') return '#ff9800';
    if (duration === 'Rejected') return '#f44336';
    if (duration === 'No Answer') return '#9e9e9e';
    return 'inherit';
  };

  const handleTranscriptClick = (transcript,user_name, call_to, call_from, call_type) => {
    try {
      const parsed = typeof transcript === 'string' ? JSON.parse(transcript) : transcript;
      setSelectedTranscript(parsed);
      setTranscriptOpen(true);
      setAgentUsername(user_name);
      if(call_type === 'outgoing'){
        setCustmerNo(call_to);
      }else{
         setCustmerNo(call_from);
      }
    } catch (error) {
      console.error('Error parsing transcript:', error);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const closeTranscriptOpen = () =>{
    setTranscriptOpen(false);
    setAgentUsername('');
    setCustmerNo('');
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <CssBaseline />
      <Typography variant="h5" sx={{ marginBottom: '15px' }}>
        Call Log
      </Typography>
      <Box sx={{ bgcolor: 'background.paper', boxShadow: 'none', border: `0.1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`, borderRadius: '5px' }}>
        <Box sx={{ padding: 2, display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
          <TextField
            label="Search"
            variant="outlined"
            fullWidth
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search"
          />
          <FormControl variant="outlined" fullWidth>
            <InputLabel>Filter</InputLabel>
            <Select value={calltypeFilter} onChange={e => setCalltypeFilter(e.target.value)} label="Filter by Role">
              <MenuItem value="">Call Type</MenuItem>
              <MenuItem value="Incoming">Incoming</MenuItem>
              <MenuItem value="Outgoing">Outgoing</MenuItem>
              <MenuItem value="Received">Answered</MenuItem>
              <MenuItem value="Missed">Missed</MenuItem>
              <MenuItem value="No Answer">No Answer</MenuItem>
              <MenuItem value="Busy">Busy</MenuItem>
              <MenuItem value="Rejected">Rejected</MenuItem>
              <MenuItem value="Timeout">Timeout</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead sx={{ bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)', width: '100%' }}>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell align="center">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">Loading...</TableCell>
                </TableRow>
              ) : data.length > 0 ? (
                data.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>{row.user_name}</TableCell>
                    <TableCell>{row.call_from}</TableCell>
                    <TableCell>{row.call_to}</TableCell>
                    <TableCell sx={{ color: `${getDurationColor(row.call_duration)} !important` }}>
                      {row.call_duration}
                    </TableCell>
                    <TableCell>{row.call_type}</TableCell>
                    <TableCell>{row.call_time}</TableCell>
                    <TableCell>
                      <Button
                        variant="contained"
                        color="info"
                        sx={{ padding: '1px', margin: '5px' }}
                        onClick={() => copyPhoneNumber(row.call_from, row.call_to)}
                      >
                        <Phone sx={{ fontSize: '1.5rem', cursor: 'pointer', margin: '5px' }} />
                      </Button>
                      {sessionStorage.getItem("user_level") !== 'agent' &&
                        row.recording_id &&
                        row.call_duration !== 'Busy' &&
                        row.call_duration !== 'Missed' &&
                        row.call_duration !== 'No Answer' &&
                        row.call_duration !== 'Failed' &&
                        row.call_duration !== 'Timeout' &&
                        row.call_duration !== 'Forbidden' &&
                        row.call_duration !== 'Rejected' && (
                          <>
                            <Button
                              variant="contained"
                              color="warning"
                              sx={{ padding: '6px', margin: '5px' }}
                              onClick={() => togglePlay(`call_${row.recording_id}`)}
                            >
                              {activeId === `call_${row.recording_id}` ? (
                                <PauseCircle sx={{ fontSize: '1.5rem', cursor: 'pointer' }} />
                              ) : (
                                <PlayArrow sx={{ fontSize: '1.5rem', cursor: 'pointer' }} />
                              )}
                            </Button>
                            <Button
                              variant="contained"
                              color="success"
                              sx={{ padding: '6px', margin: '5px' }}
                              onClick={() => handleDownload(row.call_id, row.call_from, row.call_to, `call_${row.recording_id}`)}
                            >
                              <Download />
                            </Button>
                          </>
                        )}
                      {row.transcript && (
<Tooltip title="View Transcript">
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{ padding: '6px', margin: '5px' }}
                            onClick={() => handleTranscriptClick(row.transcript, row.user_name, row.call_to, row.call_from, row.call_type)}
                          >
<DescriptionIcon />
                            </Button>
                            </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">No matching records found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalItems}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 50, 100]}
          labelRowsPerPage="Rows per page"
          sx={{
            color: theme.palette.text.primary,
          }}
        />
      </Box>

      {/* Transcript Modal - Fixed with proper dark/light mode and swapped customer/agent */}
      <Dialog
        open={transcriptOpen}
        onClose={() => closeTranscriptOpen()}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '8px',
            maxHeight: '80vh',
            bgcolor: theme.palette.background.paper,
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: theme.palette.primary.main, 
          color: theme.palette.primary.contrastText,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <DescriptionIcon />
            <Typography variant="h6">Call Transcript</Typography>
          </Box>
          <IconButton 
            onClick={() => setTranscriptOpen(false)} 
            sx={{ color: theme.palette.primary.contrastText }}
            size="small"
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ 
          p: 0, 
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]
        }}>
          <Box sx={{ 
            height: '60vh', 
            overflowY: 'auto',
            p: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}>
            {selectedTranscript && selectedTranscript.map((message, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: message.speaker === 'customer' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%',
                  alignSelf: message.speaker === 'customer' ? 'flex-end' : 'flex-start',
                }}
              >
                <Box
                  sx={{
                    bgcolor: message.speaker === 'customer' 
                      ? theme.palette.primary.main 
                      : theme.palette.mode === 'dark' 
                        ? theme.palette.grey[800] 
                        : theme.palette.grey[300],
                    color: message.speaker === 'customer' 
                      ? theme.palette.primary.contrastText 
                      : theme.palette.text.primary,
                    borderRadius: 2,
                    p: 1.5,
                    boxShadow: 1,
                    mb: 0.5,
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {message.speaker === 'customer' ? `Agent: ( ${agentUsername} )` : `Customer: ( ${custmerNo} )`}
                  </Typography>
                  <Typography variant="body1">
                    {message.text}
                  </Typography>
                </Box>
                <Chip
                  label={formatTime(message.start)}
                  size="small"
                  sx={{ 
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                    color: theme.palette.text.secondary,
                    fontSize: '0.7rem',
                    height: '20px'
                  }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default Calls;